// ✅ src/pages/VehicleV2/MonthlyWorkingHrsReport.jsx
// UPDATED FRONTEND
// - Uses localStorage session headers
// - Sends x-finyearid / x-finyear / x-branchid / x-branchtype / x-userid
// - Keeps same UI, filters, export, print, row view, pagination

import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/monthlyWorkinghrsRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

const exportToCsv = (rows, filename = "vehicle-monthly-working-hrs.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const isFutureDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const d = new Date(yyyyMmDd);
  d.setHours(0, 0, 0, 0);

  return d > today;
};

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const monthNames = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const monthIdxToKey = (idx1to12) => ({
  hr: `fld_hr${idx1to12}`,
  km: `fld_km${idx1to12}`,
});

const sumMonthly = (rows, keyPrefix) => {
  let t = 0;
  for (const r of rows || []) {
    for (let i = 1; i <= 12; i++) t += safeNum(r?.[`${keyPrefix}${i}`]);
  }
  return round2(t);
};

export default function MonthlyWorkingHrsReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin, theme } = useContext(ThemeContext);

  const currentSkin = useMemo(() => getCurrentSkin(theme || skin), [theme, skin]);
  const { baseColor, baseShade } = currentSkin || {};
  const buttonClass = `bg-${baseColor || "blue"}-${baseShade || "600"}`;
  const hoverClass = `hover:bg-${baseColor || "blue"}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const sessionBranchType = (
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO"
  ).toUpperCase();
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  const [meta, setMeta] = useState({
    branches: [],
    stores: [],
  });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    month: "Select",
    year: "Select",
    branch:
      sessionBranchType === "HO"
        ? "Select"
        : toStr(sessionBranchId || "Select"),
    store: "Select",
  });

  const [monthlyRows, setMonthlyRows] = useState([]);
  const [listRows, setListRows] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);

  const [monthlyPage, setMonthlyPage] = useState(1);
  const monthlyPageSize = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- validation ---------------- */
  const validateDates = useCallback((f) => {
    if (f.fromDate && isFutureDate(f.fromDate)) {
      toast.error("Select from date lesser than current date");
      return false;
    }
    if (f.toDate && isFutureDate(f.toDate)) {
      toast.error("Select to date lesser than current date");
      return false;
    }
    if (f.fromDate && f.toDate) {
      const a = new Date(f.fromDate).getTime();
      const b = new Date(f.toDate).getTime();
      if (b < a) {
        toast.error("Please select from date less than to date");
        return false;
      }
    }
    return true;
  }, []);

  /* ---------------- payload helpers ---------------- */
  const buildCommonParams = useCallback(
    (f) => {
      const branchToSend =
        sessionBranchType === "HO"
          ? toStr(f.branch === "Select" ? "Select" : f.branch)
          : toStr(sessionBranchId || "Select");

      return {
        branchId: toStr(sessionBranchId || ""),
        branchType: sessionBranchType || "HO",
        loginUserId: toStr(loginUserId || ""),
        fromDate: toStr(f.fromDate),
        toDate: toStr(f.toDate),
        month: toStr(f.month === "Select" ? "" : f.month),
        year: toStr(f.year === "Select" ? "" : f.year),
        branch: branchToSend,
        store: toStr(f.store === "Select" ? "Select" : f.store),
      };
    },
    [sessionBranchId, sessionBranchType, loginUserId]
  );

  /* ---------------- APIs ---------------- */
  const loadMeta = useCallback(async () => {
    try {
      const rs = await axios.get(`${API_BASE}/meta`, {
        headers: apiHeaders,
        params: {
          branchId: sessionBranchId || "",
          branchType: sessionBranchType || "HO",
          loginUserId: loginUserId || "",
        },
      });

      if (!rs.data?.success) {
        throw new Error(rs.data?.message || "Meta load failed");
      }

      setMeta({
        branches: Array.isArray(rs.data.branches) ? rs.data.branches : [],
        stores: Array.isArray(rs.data.stores) ? rs.data.stores : [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setMeta({ branches: [], stores: [] });
    }
  }, [apiHeaders, sessionBranchId, sessionBranchType, loginUserId]);

  const loadMonthlyGrid = useCallback(
    async (f, showToast = true) => {
      try {
        if (!validateDates(f)) {
          setMonthlyRows([]);
          return;
        }

        setLoadingMonthly(true);

        const rs = await axios.get(`${API_BASE}/monthly`, {
          headers: apiHeaders,
          params: buildCommonParams(f),
        });

        if (!rs.data?.success) {
          throw new Error(rs.data?.message || "Monthly grid load failed");
        }

        const list = Array.isArray(rs.data.rows) ? rs.data.rows : [];
        setMonthlyRows(list);
        setMonthlyPage(1);

        if (showToast) {
          toast.success(`Monthly grid: ${rs.data.count ?? list.length} rows`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load monthly grid");
        setMonthlyRows([]);
      } finally {
        setLoadingMonthly(false);
      }
    },
    [apiHeaders, buildCommonParams, validateDates]
  );

  const loadSearchList = useCallback(
    async (f, page = 1, pageSize = listPageSize, showToast = false) => {
      try {
        if (!validateDates(f)) {
          setListRows([]);
          setListTotal(0);
          return;
        }

        setLoadingList(true);

        const rs = await axios.get(`${API_BASE}/list`, {
          headers: apiHeaders,
          params: {
            ...buildCommonParams(f),
            page: toNum(page, 1),
            pageSize: toNum(pageSize, 10),
          },
        });

        if (!rs.data?.success) {
          throw new Error(rs.data?.message || "List load failed");
        }

        setListRows(Array.isArray(rs.data.rows) ? rs.data.rows : []);
        setListTotal(toNum(rs.data.total ?? 0, 0));
        setListPage(toNum(page, 1));
        setListPageSize(toNum(pageSize, 10));

        if (showToast) {
          toast.success(`List: ${rs.data.total ?? (rs.data.rows || []).length} records`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load list");
        setListRows([]);
        setListTotal(0);
      } finally {
        setLoadingList(false);
      }
    },
    [apiHeaders, buildCommonParams, listPageSize, validateDates]
  );

  /* ---------------- init ---------------- */
  useEffect(() => {
    const init = async () => {
      await loadMeta();

      const initialFilters = {
        fromDate: "",
        toDate: "",
        month: "Select",
        year: "Select",
        branch:
          sessionBranchType === "HO"
            ? "Select"
            : toStr(sessionBranchId || "Select"),
        store: "Select",
      };

      setFilters(initialFilters);
      await loadMonthlyGrid(initialFilters, false);
    };

    init();
  }, [loadMeta, loadMonthlyGrid, sessionBranchId, sessionBranchType]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      month: "Select",
      year: "Select",
      branch:
        sessionBranchType === "HO"
          ? "Select"
          : toStr(sessionBranchId || "Select"),
      store: "Select",
    });
    toast.info("Filters cleared");
  };

  const handleSearch = async () => {
    await loadMonthlyGrid(filters, true);
  };

  const handleViewAll = async () => {
    const next = {
      fromDate: "",
      toDate: "",
      month: "Select",
      year: "Select",
      branch:
        sessionBranchType === "HO"
          ? "Select"
          : toStr(sessionBranchId || "Select"),
      store: "Select",
    };

    setFilters(next);
    await loadSearchList(next, 1, listPageSize, true);
    await loadMonthlyGrid(next, true);
  };

  const handleExportMonthly = () => {
    if (!monthlyRows.length) {
      toast.info("No monthly rows to export");
      return;
    }
    exportToCsv(monthlyRows, "monthly-working-hrs-grid.csv");
    toast.success("Exported Monthly grid CSV");
  };

  const handleExportList = () => {
    if (!listRows.length) {
      toast.info("No list rows to export");
      return;
    }
    exportToCsv(listRows, "monthly-working-hrs-list.csv");
    toast.success("Exported List CSV");
  };

  const handlePrint = () => {
    if (!monthlyRows.length) {
      toast.info("No rows to print");
      return;
    }

    const qs = new URLSearchParams({
      branchId: String(sessionBranchId || ""),
      branchType: sessionBranchType,
      loginUserId: String(loginUserId || ""),
      fromDate: toStr(filters.fromDate),
      toDate: toStr(filters.toDate),
      month: toStr(filters.month === "Select" ? "" : filters.month),
      year: toStr(filters.year === "Select" ? "" : filters.year),
      branch:
        sessionBranchType === "HO"
          ? toStr(filters.branch === "Select" ? "Select" : filters.branch)
          : toStr(sessionBranchId || "Select"),
      store: toStr(filters.store),
    }).toString();

    window.open(`/vehicle/MonthlyWorkingHrsRpt_Print?${qs}`, "_blank");
  };

  const handleRowView = (row) => {
    const vehMonthlyIdView = pick(row, ["VehMonthlyIdView", "fld_vehicleid", "vehicleid", "reqid"]);
    const vehMonthlyId = pick(row, ["VehMonthlyId", "MonthId", "fld_monthid"]);
    const vehYearId = pick(row, ["VehYearId", "YearId", "fld_yearid"]);

    const qs = new URLSearchParams({
      vehMonthlyIdView: toStr(vehMonthlyIdView),
      vehMonthlyId: toStr(vehMonthlyId),
      vehYearId: toStr(vehYearId),
      fromDate: toStr(filters.fromDate),
      toDate: toStr(filters.toDate),
    }).toString();

    window.open(`/vehicle/MonthlyWorkingHrsView?${qs}`, "_blank");
  };

  /* ---------------- monthly client pagination ---------------- */
  const monthlyTotalPages = Math.ceil(monthlyRows.length / monthlyPageSize) || 1;
  const monthlyIndexLast = monthlyPage * monthlyPageSize;
  const monthlyIndexFirst = monthlyIndexLast - monthlyPageSize;

  const monthlyCurrentItems = useMemo(
    () => monthlyRows.slice(monthlyIndexFirst, monthlyIndexLast),
    [monthlyRows, monthlyIndexFirst, monthlyIndexLast]
  );

  /* ---------------- list server pagination ---------------- */
  const listTotalPages = Math.max(1, Math.ceil(listTotal / listPageSize));

  const gotoListPage = (p) => {
    const page = Math.min(Math.max(1, p), listTotalPages);
    loadSearchList(filters, page, listPageSize);
  };

  /* ---------------- totals ---------------- */
  const monthlyTotalHrs = useMemo(() => sumMonthly(monthlyRows, "fld_hr"), [monthlyRows]);
  const monthlyTotalKm = useMemo(() => sumMonthly(monthlyRows, "fld_km"), [monthlyRows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE MONTHLY WORKING HOURS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Monthly Working Hours Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Monthly Grid: {monthlyRows.length} Rows
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-emerald-900 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                Total Hrs: {monthlyTotalHrs} | Total KM: {monthlyTotalKm}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {monthNames.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {Array.from({ length: 25 }, (_, i) => String(2006 + i)).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={sessionBranchType !== "HO"}
                  >
                    <option value="Select">Select</option>
                    {meta.branches.map((b) => (
                      <option key={b.value ?? b.label} value={b.value ?? b.label}>
                        {b.label ?? b.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="lg:col-span-2">
                  <label className={labelClass}>Store</label>
                  <select name="store" value={filters.store} onChange={handleChange} className={inputClass}>
                    <option value="Select">Select</option>
                    {meta.stores.map((s) => (
                      <option key={s.value ?? s.label} value={s.value ?? s.label}>
                        {s.label ?? s.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-3 flex-wrap justify-end">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                    disabled={loadingMonthly || loadingList}
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loadingMonthly || loadingList}
                  >
                    <FaSearch />
                    {loadingMonthly || loadingList ? "Searching..." : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                  >
                    <FaPrint />
                    Print
                  </button>

                  <button
                    type="button"
                    onClick={handleExportMonthly}
                    className={`flex items-center gap-2 border px-6 py-3 rounded-lg font-semibold ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaFileExport />
                    Export Monthly
                  </button>

                  <button
                    type="button"
                    onClick={handleExportList}
                    className={`flex items-center gap-2 border px-6 py-3 rounded-lg font-semibold ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaFileExport />
                    Export List
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Monthly Working Hrs/KM (Apr → Mar)
              </h2>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {loadingMonthly ? "Loading..." : `Rows: ${monthlyRows.length}`}
              </div>
            </div>

            <div
              className={`rounded-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {monthlyRows.length === 0 ? (
                <div className="text-center py-14">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                      isDark ? "bg-gray-700" : "bg-gray-100"
                    }`}
                  >
                    <FaInfoCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className={`text-lg font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    No Records Found
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th rowSpan={2} className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                          S.No
                        </th>
                        <th rowSpan={2} className="px-3 py-3 text-left font-semibold whitespace-nowrap">
                          Branch
                        </th>
                        <th rowSpan={2} className="px-3 py-3 text-left font-semibold whitespace-nowrap">
                          Vehicle Name
                        </th>
                        <th rowSpan={2} className="px-3 py-3 text-left font-semibold whitespace-nowrap">
                          Vehicle Type
                        </th>

                        {monthNames.map((m) => (
                          <th key={m} colSpan={2} className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                            {m}
                          </th>
                        ))}
                      </tr>

                      <tr>
                        {monthNames.map((m) => (
                          <React.Fragment key={`${m}-sub`}>
                            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">Hrs</th>
                            <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">KM</th>
                          </React.Fragment>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {monthlyCurrentItems.map((r, idx) => {
                        const branch = pick(r, ["Branch", "branch", "fld_branchname"]);
                        const vehName = pick(r, ["vehicle_name", "VehicleName", "vehiclename"]);
                        const vehType = pick(r, ["vehicletype", "VehicleType"]);

                        return (
                          <tr
                            key={`${branch}-${vehName}-${idx}`}
                            className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                          >
                            <td className={`px-3 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {monthlyIndexFirst + idx + 1}
                            </td>
                            <td className={`px-3 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {branch || "-"}
                            </td>
                            <td className={`px-3 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {vehName || "-"}
                            </td>
                            <td className={`px-3 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {vehType || "-"}
                            </td>

                            {Array.from({ length: 12 }, (_, i0) => {
                              const i = i0 + 1;
                              const k = monthIdxToKey(i);
                              const hrs = pick(r, [k.hr], "0.00");
                              const km = pick(r, [k.km], "0.00");

                              return (
                                <React.Fragment key={`${idx}-${i}`}>
                                  <td
                                    className={`px-3 py-3 text-sm text-right whitespace-nowrap ${
                                      isDark ? "text-gray-200" : "text-gray-900"
                                    }`}
                                  >
                                    {hrs}
                                  </td>
                                  <td
                                    className={`px-3 py-3 text-sm text-right whitespace-nowrap ${
                                      isDark ? "text-gray-200" : "text-gray-900"
                                    }`}
                                  >
                                    {km}
                                  </td>
                                </React.Fragment>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>

                    <tfoot>
                      <tr className={`${isDark ? "bg-gray-900/40" : "bg-gray-50"}`}>
                        <td className={`px-3 py-3 text-sm font-semibold text-center ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                        <td className={`px-3 py-3 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                        <td className={`px-3 py-3 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                        <td className={`px-3 py-3 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          Totals
                        </td>

                        <td className={`px-3 py-3 text-sm font-semibold text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {monthlyTotalHrs}
                        </td>
                        <td className={`px-3 py-3 text-sm font-semibold text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {monthlyTotalKm}
                        </td>

                        {Array.from({ length: 11 }, (_, i) => (
                          <React.Fragment key={`ft-${i}`}>
                            <td className="px-3 py-3" />
                            <td className="px-3 py-3" />
                          </React.Fragment>
                        ))}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {monthlyRows.length > 0 && (
              <div className="mt-5 flex flex-col sm:flex-row justify-between items-center">
                <div className={`text-sm mb-3 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Showing {monthlyIndexFirst + 1} to {Math.min(monthlyIndexLast, monthlyRows.length)} of {monthlyRows.length} entries
                </div>

                <nav className="flex gap-1">
                  <button
                    onClick={() => setMonthlyPage((p) => Math.max(1, p - 1))}
                    disabled={monthlyPage === 1}
                    className={`px-3 py-1 border rounded text-sm ${
                      monthlyPage === 1
                        ? isDark
                          ? "border-gray-700 text-gray-500 cursor-not-allowed"
                          : "border-gray-300 text-gray-400 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, monthlyTotalPages) }, (_, i) => {
                    let pageNum;
                    if (monthlyTotalPages <= 5) pageNum = i + 1;
                    else if (monthlyPage <= 3) pageNum = i + 1;
                    else if (monthlyPage >= monthlyTotalPages - 2) pageNum = monthlyTotalPages - 4 + i;
                    else pageNum = monthlyPage - 2 + i;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setMonthlyPage(pageNum)}
                        className={`px-3 py-1 border rounded text-sm ${
                          monthlyPage === pageNum
                            ? `${buttonClass} text-white`
                            : isDark
                            ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                            : "border-gray-300 text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setMonthlyPage((p) => Math.min(monthlyTotalPages, p + 1))}
                    disabled={monthlyPage === monthlyTotalPages}
                    className={`px-3 py-1 border rounded text-sm ${
                      monthlyPage === monthlyTotalPages
                        ? isDark
                          ? "border-gray-700 text-gray-500 cursor-not-allowed"
                          : "border-gray-300 text-gray-400 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Search List (Row View / Print)
              </h2>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {loadingList ? "Loading..." : `Total: ${listTotal}`}
              </div>
            </div>

            <div
              className={`rounded-xl shadow-sm border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              {listRows.length === 0 ? (
                <div className="text-center py-14">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                      isDark ? "bg-gray-700" : "bg-gray-100"
                    }`}
                  >
                    <FaInfoCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <div className={`text-lg font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                    No Records Found
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${buttonClass} text-white`}>
                      <tr>
                        <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                        <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Branch</th>
                        <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Name</th>
                        <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Type</th>
                        <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">View</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {listRows.map((r, idx) => {
                        const branch = pick(r, ["Branch", "branch"]);
                        const vehName = pick(r, ["vehiclename", "vehicle_name", "VehicleName"]);
                        const vehType = pick(r, ["vehicletype", "VehicleType"]);

                        return (
                          <tr
                            key={`${branch}-${vehName}-${idx}`}
                            className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                          >
                            <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {(listPage - 1) * listPageSize + idx + 1}
                            </td>
                            <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {branch || "-"}
                            </td>
                            <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {vehName || "-"}
                            </td>
                            <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {vehType || "-"}
                            </td>
                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => handleRowView(r)}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                                title="View / Print"
                              >
                                <FaPrint />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {listTotal > 0 && (
              <div className="mt-5 flex flex-col sm:flex-row justify-between items-center">
                <div className={`text-sm mb-3 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Page {listPage} of {listTotalPages} (Total {listTotal})
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={listPageSize}
                    onChange={(e) => {
                      const ps = toNum(e.target.value, 10);
                      setListPageSize(ps);
                      gotoListPage(1);
                    }}
                    className={inputClass}
                    style={{ width: 110 }}
                  >
                    {[5, 10, 20, 50].map((n) => (
                      <option key={n} value={n}>
                        {n}/page
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => gotoListPage(listPage - 1)}
                    disabled={listPage <= 1}
                    className={`px-3 py-1 border rounded text-sm ${
                      listPage <= 1
                        ? isDark
                          ? "border-gray-700 text-gray-500 cursor-not-allowed"
                          : "border-gray-300 text-gray-400 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Previous
                  </button>

                  <button
                    onClick={() => gotoListPage(listPage + 1)}
                    disabled={listPage >= listTotalPages}
                    className={`px-3 py-1 border rounded text-sm ${
                      listPage >= listTotalPages
                        ? isDark
                          ? "border-gray-700 text-gray-500 cursor-not-allowed"
                          : "border-gray-300 text-gray-400 cursor-not-allowed"
                        : isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}